import {
  Avatar,
  AvatarColorOption,
  Icon,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ReactNode } from 'react';

import styles from './compactActivityEntry.module.scss';

type CompactActivityEntryProps = {
  readonly icon: IconNames;
  readonly color: AvatarColorOption;
  readonly title: ReactNode;
  readonly timestamp: ReactNode;
  readonly author?: ReactNode;
  readonly summary?: ReactNode;
  /** Revealed under the entry when it's open. */
  readonly details?: ReactNode;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
};

/**
 * The sidebar's layout of a Customer Activities entry: the timestamp shares
 * the title row and the expand chevron sits right under it, so the summary
 * gets the full width.
 */
export const CompactActivityEntry = ({
  icon,
  color,
  title,
  timestamp,
  author,
  summary,
  details,
  isOpen,
  onToggle,
}: CompactActivityEntryProps): JSX.Element => {
  const expandable = details != null;

  return (
    <div className={styles.entry}>
      <div
        className={classnames(styles.header, {
          [styles.expandable]: expandable,
        })}
        onClick={expandable ? onToggle : undefined}
      >
        <Avatar icon={icon} color={color} size="24" />
        <Text.Paragraph as="div" variant="3" className={styles.body}>
          <span className={styles.titleRow}>
            <span className={styles.title}>{title}</span>
            <span className={styles.titleAside}>
              {timestamp}
              {expandable && (
                <button
                  type="button"
                  className={styles.chevron}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                >
                  <Icon name={isOpen ? 'angle-down' : 'angle-right'} />
                </button>
              )}
            </span>
          </span>
          {author}
          {(summary || expandable) && (
            <span
              className={classnames(styles.summaryRow, {
                [styles.besideChevron]: expandable,
              })}
            >
              {summary}
            </span>
          )}
        </Text.Paragraph>
      </div>
      {isOpen && details}
    </div>
  );
};
